import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { apiClient } from "@/api/apiClient";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await apiClient.auth.requestPasswordReset({ email });
            setIsSubmitted(true);
            toast.success("Email envoyé !");
        } catch (error: any) {
            toast.error("Une erreur est survenue, veuillez réessayer plus tard.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)]">
                <Card className="w-[350px]">
                    <CardHeader>
                        <CardTitle className="text-green-600">Email envoyé !</CardTitle>
                        <CardDescription>
                            Si un compte est associé à <strong>{email}</strong>, vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Pensez à vérifier vos spams. Le lien est valide pendant 1 heure.
                        </p>
                    </CardContent>
                    <CardFooter>
                        <div className="w-full flex flex-col gap-2">
                            <Link to="/login">
                                <Button variant="outline" className="w-full">
                                    Retour à la connexion
                                </Button>
                            </Link>
                        </div>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Cast components to any to avoid TS errors
    const LabelAny = Label as any;
    const InputAny = Input as any;

    return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-[hsl(228,14%,6%)]">
            <Card className="w-[350px]">
                <CardHeader>
                    <CardTitle>Mot de passe oublié ?</CardTitle>
                    <CardDescription>
                        Entrez votre email pour recevoir un lien de réinitialisation.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent>
                        <div className="grid w-full items-center gap-4">
                            <div className="flex flex-col space-y-1.5">
                                <LabelAny htmlFor="email">Email</LabelAny>
                                <InputAny
                                    id="email"
                                    type="email"
                                    placeholder="nom@exemple.com"
                                    value={email}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <Button className="w-full" type="submit" disabled={isLoading}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Envoyer le lien
                        </Button>
                        <Link to="/login" className="flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-white">
                            <ArrowLeft className="mr-2 h-4 w-4" /> Retour à la connexion
                        </Link>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
};

export default ForgotPasswordPage;
